import type { MsgPayloadType } from '@halo/chat-sdk';
import { type Msg } from '@halo/chat-sdk';
import { getFileBaseInfo, getPictureInfo } from '@halo/chat-sdk';
import { browserDownloadFile } from '@halo/utils';
import { type StateCreatorEnhance } from '@halo/zustand-enhance';
import type { SessionStore } from './main';

type FileInfo = string;

export interface FileMsgInfo {
  file?: FileInfo;
  progress: number;
  process?: Promise<FileInfo | null>;
}

export interface FileMsgsStore {
  /**
   * cache in memory
   */
  fileMsgs: Record<string, FileMsgInfo>;
  /**
   * init
   */
  createFileMsgs: () => void;
  /**
   * send message
   */
  sendFileMessage: (
    payload_type: MsgPayloadType.FILE | MsgPayloadType.IMAGE,
    file: File,
    saveInMemo?: boolean
  ) => Promise<void>;
  /**
   * get message file (save)
   */
  getFile: (msg: Msg) => Promise<string | null>;
  /**
   * get message file and download (don't save)
   */
  downloadFile: (msg: Msg) => Promise<void>;
  /**
   * cancel get file
   */
  cancelFile: (cli_msg_id: string) => Promise<void>;
  /**
   * remove file msg
   */
  removeFileMsg: (cli_msg_id: string) => void;
  /**
   * clear all
   */
  clearFileMsgs: () => void;
}

export const fileMsgsStoreCreator: StateCreatorEnhance<SessionStore> = (set, get) =>
  ({
    fileMsgs: {},
    createFileMsgs: () => {
      const { client, session, effects, removeFileMsg, clearFileMsgs } = get();
      if (client && session) {
        effects.push(
          // change progress
          client.addEventListener(
            'FileTransferProgress',
            ({ detail: { cli_msg_id, progress: _progress } }) => {
              let progress = _progress;
              if (progress > 99) progress = 99;
              set((store) => {
                const prev = store.fileMsgs[cli_msg_id];
                if (prev?.progress && prev.progress > progress) return;
                store.fileMsgs[cli_msg_id] = {
                  ...prev,
                  progress,
                };
              });
            }
          ),
          // set progress 100
          // client.addEventListener('FileTransferComplete', ({ detail: { cli_msg_id } }) => {
          //   set((store) => {
          //     const prev = store.fileMsgs[cli_msg_id];
          //     store.fileMsgs[cli_msg_id] = {
          //       ...prev,
          //       progress: 100,
          //     };
          //   });
          // }),
          // transfer error remove file msg
          client.addEventListener('FileTransferError', ({ detail: { cli_msg_id } }) => {
            removeFileMsg(cli_msg_id);
          }),
          () => {
            clearFileMsgs();
          }
        );
      }
    },
    sendFileMessage: async (payload_type, file, saveInMemo = false) => {
      const { client, session } = get();
      if (!client || !session) {
        return;
      }

      let url: string;
      if (saveInMemo) url = URL.createObjectURL(file);
      try {
        const fileInfo = file.type.includes('image')
          ? await getPictureInfo(file)
          : await getFileBaseInfo(file);

        const { cli_msg_id } = await client.Api.messages.sendMsg({
          chat_id: session.chat_id,
          payload: fileInfo,
          payload_type,
          file_name: file.name,
          file_data: new Uint8Array(await file.arrayBuffer()),
        });

        set((store) => {
          const prev = store.fileMsgs[cli_msg_id];
          store.fileMsgs[cli_msg_id] = {
            ...prev,
            file: url,
            progress: 100,
          };
        });
      } catch (e) {
        if (url) URL.revokeObjectURL(url);
        throw e;
      }
    },
    getFile: async (msg) => {
      if (typeof msg.payload.meta !== 'string') {
        return null;
      }
      const { client, session, fileMsgs, removeFileMsg } = get();
      if (!client || !session) return null;

      const cli_msg_id = msg.cli_msg_id;
      const cached = fileMsgs[cli_msg_id];

      if (cached?.file) {
        return cached.file;
      }

      if (cached?.process) {
        return cached.process;
      }

      const process = new Promise<string | null>((resolve) => {
        client.Api.file
          .download({
            chat_id: session.chat_id,
            cli_msg_id,
            meta: msg.payload?.meta,
          })
          .then((file) => {
            if (!file) return resolve(null);
            set((store) => {
              const prev = store.fileMsgs[cli_msg_id];
              if (!prev) return resolve(null);
              const url = URL.createObjectURL(file);

              store.fileMsgs[cli_msg_id] = {
                file: url,
                progress: 100,
                process: undefined,
              };
              resolve(url);
            });
          })
          .catch(() => {
            removeFileMsg(cli_msg_id);
            resolve(null);
          });
      });

      set((store) => {
        store.fileMsgs[cli_msg_id] = {
          progress: 0,
          process: process,
        };
      });

      return process;
    },
    downloadFile: async (msg) => {
      const { client, session, fileMsgs, removeFileMsg } = get();
      if (!client || !session) return;
      try {
        const cli_msg_id = msg.cli_msg_id;
        const cached = fileMsgs[cli_msg_id];
        if (cached?.file) {
          const response = await fetch(cached.file);
          const blob = await response.blob();
          const file = new File([blob], msg.payload.name || 'download', {
            type: msg.payload.type || blob.type,
          });
          browserDownloadFile(file);
          return;
        }

        const url = await get().getFile(msg);
        if (!url) throw new Error();

        const response = await fetch(url);
        const blob = await response.blob();
        const file = new File([blob], msg.payload.name || 'file', {
          type: msg.payload.type || blob.type,
        });
        browserDownloadFile(file);
      } catch {
        throw new Error();
      } finally {
        removeFileMsg(msg.cli_msg_id);
      }
    },
    cancelFile: async (cli_msg_id: string) => {
      const { client, session, removeFileMsg } = get();
      if (client && session) {
        await client.Api.file.cancel({ cli_msg_id: cli_msg_id });
        removeFileMsg(cli_msg_id);
      }
    },
    removeFileMsg: (cli_msg_id) => {
      set((store) => {
        const prev = store.fileMsgs[cli_msg_id];
        if (prev?.file) {
          URL.revokeObjectURL(prev.file);
        }
        store.fileMsgs[cli_msg_id] = null;
      });
    },
    clearFileMsgs: () => {
      set((store) => {
        Object.keys(store.fileMsgs).forEach((cli_msg_id) => {
          const prev = store.fileMsgs[cli_msg_id];
          if (prev?.file) {
            URL.revokeObjectURL(prev.file);
          }
          store.fileMsgs[cli_msg_id] = null;
        });
      });
    },
  }) as SessionStore;
